export interface SimpleCommerceCartItem {
    id: string
    tax: {
        rate: number
        amount: number
        price_includes_tax: boolean
    }
    total: number
    product: number
    variant: {
        product: string
        variant: string
    }
    metadata: []
    quantity: number
}

export interface SimpleCommerceCart {
    billing_address?: string
    billing_address_line2?: string
    billing_city?: string
    billing_country?: string
    billing_name?: string
    billing_postal_code?: string
    billing_region?: string
    coupon?: string
    coupon_total: number
    created_at: string
    customer_id?: string
    data: any[]
    gateway?: string
    grand_total: number
    id: number
    items: SimpleCommerceCartItem[]
    items_total: number
    order_date?: string
    order_number: number
    order_status: string
    payment_status: string
    shipping_address?: string
    shipping_address_line2?: string
    shipping_city?: string
    shipping_country?: string
    shipping_name?: string
    shipping_postal_code?: string
    shipping_region?: string
    shipping_total: number
    tax_total: number
    updated_at: string
    use_shipping_address_for_billing: boolean
}

export interface SimpleCommerceCustomer {
    id?: string
    name?: string
    email?: string
    phone?: string
}

export interface SimpleCommerceShippingAddress {
    use_shipping_address_for_billing?: boolean
    shipping_name?: string
    shipping_address_line1: string
    shipping_address_line2?: string
    shipping_city: string
    shipping_region: string
    shipping_postal_code: string
    shipping_country?: string
}

export const useSimpleCommerceCart = () => {
    const simpleCommerce = useSimpleCommerce()

    const useOrderState = () =>
        useState<'pending' | 'complete'>('orderState', () => 'pending')

    const useCartData = () =>
        useState<SimpleCommerceCart | undefined>('cartData', () => undefined)

    const useCartCount = () => useState('cartCount', () => 0)

    const useCartItems = () =>
        useState<SimpleCommerceCartItem[]>('cartItems', () => [])

    const setCartData = (data: SimpleCommerceCart) => {
        useCartData().value = data
        useCartCount().value = data?.items.length ?? 0
        useCartItems().value = data?.items ?? []
    }

    const fetchCartData = async (refresh = false) => {
        if (useCartData().value && !refresh) {
            return
        }

        const { data }: { data: SimpleCommerceCart } =
            await simpleCommerce('/cart')

        setCartData(data)
    }

    const addProduct = async (product, quantity = 1, variant) => {
        const { data }: { data: SimpleCommerceCart } = await simpleCommerce(
            '/cart-items',
            {
                method: 'POST',
                body: {
                    _request: 'Empty',
                    _error_redirect: 'Empty',
                    _redirect: 'Empty',
                    product: product.id.toString(),
                    quantity,
                    variant,
                },
            },
        )

        setCartData(data)
    }

    const removeItem = async (item: SimpleCommerceCartItem) => {
        const { cart }: { cart: SimpleCommerceCart } = await simpleCommerce(
            `/cart-items/${item.id}`,
            {
                method: 'DELETE',
            },
        )

        setCartData(cart)
    }

    const updateItemQuantity = async (
        item: SimpleCommerceCartItem,
        quantity: number,
    ) => {
        const { cart }: { cart: SimpleCommerceCart } = await simpleCommerce(
            `/cart-items/${item.id}`,
            {
                method: 'POST',
                body: {
                    _request: 'Empty',
                    _error_redirect: 'Empty',
                    _redirect: 'Empty',
                    quantity,
                },
            },
        )

        setCartData(cart)
    }

    const checkout = async (formData: Record<string, any>) => {
        const res = await simpleCommerce('/checkout', {
            method: 'POST',
            body: {
                _request: 'Empty',
                _error_redirect: 'Empty',
                _redirect: 'Empty',
                use_shipping_address_for_billing: true,
                customer: {
                    email: formData.email,
                    name: formData.name,
                    phone: formData.phone,
                },
                gateway: formData.gateway,
                shipping_address_line1: formData.shipping_address_line1,
                shipping_address_line2: formData.shipping_address_line2,
                shipping_city: formData.shipping_city,
                shipping_region: formData.shipping_region,
                shipping_postal_code: formData.shipping_postal_code,
                shipping_country: 'AU',
            },
        })

        useOrderState().value = 'complete'
    }

    return {
        useOrderState,
        useCartData,
        useCartCount,
        useCartItems,
        fetchCartData,
        addProduct,
        removeItem,
        updateItemQuantity,
        checkout,
    }
}
